import type { EnumValueMeta } from '@joyful/utils'
import { createNonNullableContext, createEnumSchema } from '@joyful/utils'
import { MonitorIcon, MoonIcon, SunIcon } from 'lucide-react'
import type { ComponentType, ReactNode, SVGProps } from 'react'
import { useEffect, useSyncExternalStore } from 'react'
export interface ThemeProviderProps {
  children: ReactNode
}

export type Theme = 'light' | 'dark' | 'system'

export const ThemeSchema = createEnumSchema<
  Theme,
  EnumValueMeta & { icon: ComponentType<SVGProps<SVGSVGElement>> }
>([
  {
    icon: SunIcon,
    title: 'Light',
    value: 'light',
  },
  {
    icon: MoonIcon,
    title: 'Dark',
    value: 'dark',
  },
  {
    icon: MonitorIcon,
    title: 'System',
    value: 'system',
  },
])

const DARK_MQ = '(prefers-color-scheme: dark)'
const DefaultTheme: Theme = 'light'
const ThemeCookieName = 'ui-theme'
const ThemeCookieChangedEvent = 'joyful-ui-theme-change'
const ThemeCookieMaxAgeSeconds = 60 * 60 * 24 * 365

function subscribeToSystemTheme(callback: () => void) {
  const mql = window.matchMedia(DARK_MQ)
  mql.addEventListener('change', callback)
  return () => {
    mql.removeEventListener('change', callback)
  }
}

function getSystemPrefersDark() {
  return window.matchMedia(DARK_MQ).matches
}

function getSystemPrefersDarkServerSnapshot() {
  return false
}

interface ThemeProviderState {
  isDarkMode: boolean
  setTheme: (theme: Theme) => void
  theme: Theme
}

const ThemeProviderContext = createNonNullableContext<ThemeProviderState>(
  'ThemeProviderContext',
)

export function ThemeProvider({ children }: ThemeProviderProps) {
  const theme = useSyncExternalStore(
    subscribeToThemeCookie,
    getThemeCookieSnapshot,
    getThemeCookieServerSnapshot,
  )

  const systemPrefersDark = useSyncExternalStore(
    subscribeToSystemTheme,
    getSystemPrefersDark,
    getSystemPrefersDarkServerSnapshot,
  )

  const isDarkMode = isThemeDark({ systemPrefersDark, theme })

  useEffect(() => {
    setThemeCookie(theme)
    syncThemeClass({ systemPrefersDark, theme })
  }, [systemPrefersDark, theme])

  const setTheme = (toTheme: Theme) => {
    setThemeCookie(toTheme)
    syncThemeClass({ systemPrefersDark, theme: toTheme })
    globalThis.dispatchEvent(new Event(ThemeCookieChangedEvent))
  }

  return (
    <ThemeProviderContext.Context value={{ isDarkMode, setTheme, theme }}>
      {children}
    </ThemeProviderContext.Context>
  )
}

export const useTheme = ThemeProviderContext.useRequiredContext

function subscribeToThemeCookie(onChange: () => void) {
  globalThis.addEventListener(ThemeCookieChangedEvent, onChange)
  globalThis.addEventListener('focus', onChange)
  document.addEventListener('visibilitychange', onChange)

  return () => {
    globalThis.removeEventListener(ThemeCookieChangedEvent, onChange)
    globalThis.removeEventListener('focus', onChange)
    document.removeEventListener('visibilitychange', onChange)
  }
}

function getThemeCookieSnapshot() {
  return getThemeCookie() ?? DefaultTheme
}

function getThemeCookieServerSnapshot() {
  return DefaultTheme
}

function getThemeCookie() {
  const cookiePrefix = `${ThemeCookieName}=`
  const themeCookie = document.cookie.split('; ').find((cookiePart) => {
    return cookiePart.startsWith(cookiePrefix)
  })
  if (!themeCookie) {
    return null
  }

  const encodedValue = themeCookie.slice(cookiePrefix.length)
  try {
    const decodedValue = decodeURIComponent(encodedValue)
    const parsedTheme = ThemeSchema.schema.safeParse(decodedValue)
    return parsedTheme.success === true ? parsedTheme.data : null
  } catch {
    return null
  }
}

function setThemeCookie(theme: Theme) {
  const cookieParts = [
    `${ThemeCookieName}=${encodeURIComponent(theme)}`,
    'Path=/',
    `Max-Age=${ThemeCookieMaxAgeSeconds}`,
    'SameSite=Lax',
  ]
  const cookieDomain = getThemeCookieDomain()
  if (cookieDomain) {
    cookieParts.push(`Domain=${cookieDomain}`)
  }

  if (globalThis.location.protocol === 'https:') {
    cookieParts.push('Secure')
  }

  document.cookie = cookieParts.join('; ')
}

function getThemeCookieDomain() {
  const hostname = globalThis.location.hostname
  if (
    hostname === 'joyfulhealth.com' ||
    hostname.endsWith('.joyfulhealth.com')
  ) {
    return '.joyfulhealth.com'
  }

  if (hostname === 'joyfulhealth.io' || hostname.endsWith('.joyfulhealth.io')) {
    return '.joyfulhealth.io'
  }

  return null
}

function isThemeDark({
  systemPrefersDark,
  theme,
}: {
  systemPrefersDark: boolean
  theme: Theme
}) {
  return theme === 'dark' || (theme === 'system' && systemPrefersDark)
}

function syncThemeClass({
  systemPrefersDark,
  theme,
}: {
  systemPrefersDark: boolean
  theme: Theme
}) {
  const isDark = isThemeDark({ systemPrefersDark, theme })
  document.documentElement.classList.toggle('dark', isDark)
}
